import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  Product,
  AddToCartEvent,
  LOW_STOCK_THRESHOLD,
  PREPURCHASE_SOLD_OUT_MESSAGE,
  VARIANT_LABELS,
  getPrepurchaseStock,
  isEarlyAccessProduct,
  isPrepurchaseSoldOut,
} from '../../models/product.model';
import { vipBadgeText } from '../../perron-points/perron-points-copy';
import { earlyAccessThresholdOf } from '../../perron-points/early-access-gate';
import { PointsService } from '../../services/points.service';
import { RouterLink } from '@angular/router';
import { X, Minus, Plus, ShoppingCart, Bell, BellOff, CalendarClock, RotateCcw, LucideIconData } from 'lucide-angular';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { BadgeComponent } from '../badge/badge.component';
import { VariantBadgeComponent } from '../variant-badge/variant-badge.component';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { WishlistButtonComponent } from '../wishlist-button/wishlist-button.component';
import { ToastService } from '../../services/toast.service';
import {
  formatPrice,
  formatOriginalPrice,
  calcDiscountPercent,
  stripHtml,
  displayLanguage,
  formatCondition,
  getConditionTone,
  formatPowerToughness,
  splitSubtype,
  isSingleProduct,
} from '../../utils/product.utils';
import { deriveProductLeafCategoryLink } from '../../../features/catalog/data/catalog-mappers';

@Component({
  selector: 'app-product-zoom-modal',
  imports: [
    RouterLink,
    ButtonComponent,
    IconComponent,
    BadgeComponent,
    VariantBadgeComponent,
    TooltipDirective,
    WishlistButtonComponent,
  ],
  templateUrl: './product-zoom-modal.component.html',
  styleUrl: './product-zoom-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
    '(document:keydown.escape)': 'onClose()',
  },
})
export class ProductZoomModalComponent {
  private readonly toastService = inject(ToastService);
  private readonly pointsService = inject(PointsService);
  protected readonly prepurchaseSoldOutMessage = PREPURCHASE_SOLD_OUT_MESSAGE;

  product = input.required<Product>();
  /**
   * "Ya estoy suscrito al aviso de stock". Sin esto el modal siempre pintaba
   * "Avisarme cuando esté disponible", así que en superficies que sí conocen
   * el estado la tarjeta decía "Ya no me avises" y el quickview del mismo
   * producto decía lo contrario.
   *
   * A diferencia de la ProductCard, el modal **no** consulta el
   * `StockAlertStore` por su cuenta: el estado activo solo llega por este
   * input. Así, quien pase `notifyActive` está obligado a enlazar también
   * `notifyCancel`, y un «Ya no me avises» nunca se queda sin destino. Cada
   * superficie lo alimenta con `StockAlertStore.hasAlert()` (catálogo, home,
   * landing de juego, PDP, lista de deseos). Precompras no
   * lo necesita: todo es pre-sale y el modal no pinta la campana.
   */
  notifyActive = input(false);

  close = output<void>();
  addToCart = output<AddToCartEvent>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  viewDetails = output<Product>();
  /**
   * CTA "Reservar" cuando el producto es pre-sale. El componente padre debe
   * cerrar el zoom y abrir el PrepurchaseModal. Emitimos el qty elegido para
   * que el modal arranque con esa cantidad.
   */
  reserveRequest = output<AddToCartEvent>();

  protected quantity = signal(1);
  protected isFlipped = signal(false);

  protected readonly closeIcon: LucideIconData = X;
  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly cartIcon: LucideIconData = ShoppingCart;
  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly bellOffIcon: LucideIconData = BellOff;
  protected readonly calendarIcon: LucideIconData = CalendarClock;
  protected readonly rotateIcon: LucideIconData = RotateCcw;

  protected hasBackImage = computed(
    () => this.product().hasDoubleFace === true && !!this.product().backImageUrl,
  );

  protected toggleFlip(): void {
    this.isFlipped.update((v) => !v);
  }

  /**
   * Para pre-sale el stock efectivo vive en `prepurchase_stock`. Sin este
   * ajuste, una carta en precompra se muestra como "agotada" aunque tenga
   * cupos disponibles.
   */
  protected isOutOfStock = computed(() => {
    const p = this.product();
    if (p.isPreSale) return getPrepurchaseStock(p) <= 0;
    return p.stock === 0;
  });

  protected isPrepurchaseSoldOut = computed(() =>
    isPrepurchaseSoldOut(this.product()),
  );

  /** Badge «Acceso VIP» (Perrón Points) — desde R7 no depende de precompra. */
  protected isEarlyAccess = computed(() => isEarlyAccessProduct(this.product()));

  /** Badge de identidad VIP, con su variante de umbral 0 (ver `product-card`). */
  protected readonly eaBadgeText = computed(() =>
    vipBadgeText(
      earlyAccessThresholdOf(this.product(), this.pointsService.displayThreshold())
        ?? this.pointsService.displayThreshold(),
    ),
  );

  protected isLowStock = computed(() => {
    const p = this.product();
    if (p.isPreSale) return false;
    const stock = p.stock;
    return stock > 0 && stock <= LOW_STOCK_THRESHOLD;
  });

  protected readonly descriptionMaxLength = 150;

  protected hasShortDescription = computed(() => {
    const sd = this.product().shortDescription;
    return !!sd && sd.trim().length > 0;
  });

  // `description` viene de Quill (HTML). En el quickview mostramos un preview
  // truncado: convertimos a texto plano antes de medir/cortar para no enseñar
  // tags ni romper una etiqueta por la mitad.
  private readonly descriptionPlainText = computed(() =>
    stripHtml(this.product().description ?? ''),
  );

  protected hasDescription = computed(() => this.descriptionPlainText().length > 0);

  protected descriptionNeedsTruncation = computed(
    () => this.descriptionPlainText().length > this.descriptionMaxLength,
  );

  protected truncatedDescription = computed(() => {
    const d = this.descriptionPlainText();
    if (d.length <= this.descriptionMaxLength) return d;
    return d.slice(0, this.descriptionMaxLength).trimEnd() + '…';
  });

  protected discountPercent = computed(() => calcDiscountPercent(this.product()));

  protected formattedPrice = computed(() => formatPrice(this.product()));

  protected formattedOriginalPrice = computed(() => formatOriginalPrice(this.product()));

  protected formattedLanguage = computed(() => displayLanguage(this.product()));

  protected readonly isSingle = computed(() => isSingleProduct(this.product()));

  protected conditionLabel = computed(() => formatCondition(this.product().condition));
  protected conditionTone = computed(() => getConditionTone(this.product().condition));

  protected variantLabel = computed(() => VARIANT_LABELS[this.product().variant]);

  /**
   * Etiqueta "Poder" / "P/T" según el juego: OP sólo tiene power, MTG tiene
   * ambos. Si hay toughness combinamos como "5/5".
   */
  protected powerToughness = computed(() =>
    formatPowerToughness(this.product().power, this.product().toughness),
  );

  protected powerToughnessLabel = computed(() =>
    this.product().toughness ? 'P/T' : 'Poder',
  );

  /**
   * Subtipos parseados desde el string CSV de OP/MTG. Array vacío esconde la
   * sección — `@if (subtypeChips().length)`.
   */
  protected subtypeChips = computed(() => splitSubtype(this.product().subtype));

  /**
   * Token semántico de color para el dot del chip OP. Mapea los 6 colores
   * canónicos a tokens del design system; cualquier otro valor (o ausencia)
   * deja el dot neutro vía data-color="other".
   */
  protected colorChipKey = computed<string>(() => {
    const c = this.product().color?.toLowerCase().trim();
    if (!c) return 'other';
    if (['yellow', 'red', 'blue', 'green', 'black', 'purple'].includes(c)) return c;
    return 'other';
  });

  /** Link a la subcategoría hoja (set/expansión) — para hacer clicable `setName`. */
  protected setLink = computed<string | null>(() =>
    deriveProductLeafCategoryLink(this.product()),
  );

  protected canDecrement = computed(() => this.quantity() > 1);
  protected canIncrement = computed(() => {
    const p = this.product();
    // En pre-sale el tope lo dicta `min(prepurchase_stock, prepurchase_limit)`.
    // El modal de precompra re-valida esto al enviar, pero el zoom respeta
    // el límite desde aquí para que el usuario no pueda teclear un valor
    // imposible desde el selector.
    if (p.isPreSale) {
      const stock = getPrepurchaseStock(p);
      const perClient = p.prepurchase_limit ?? 0;
      const max = perClient > 0 ? Math.min(stock, perClient) : stock;
      return this.quantity() < max;
    }
    return this.quantity() < p.stock;
  });

  protected increment(): void {
    if (this.canIncrement()) {
      this.quantity.update(q => q + 1);
    }
  }

  protected decrement(): void {
    if (this.canDecrement()) {
      this.quantity.update(q => q - 1);
    }
  }

  protected onAddToCart(): void {
    this.addToCart.emit({ product: this.product(), quantity: this.quantity() });
  }

  protected onReserve(): void {
    this.reserveRequest.emit({ product: this.product(), quantity: this.quantity() });
  }

  protected onPrepurchaseSoldOutClick(): void {
    this.toastService.show({
      type: 'info',
      message: PREPURCHASE_SOLD_OUT_MESSAGE,
    });
  }

  protected onNotify(): void {
    this.notifyRequest.emit(this.product());
  }

  protected onNotifyCancel(): void {
    this.notifyCancel.emit(this.product());
  }

  protected onClose(): void {
    this.close.emit();
  }

  protected onViewDetails(): void {
    this.viewDetails.emit(this.product());
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
